import type { AnswerValue } from "./answerSet";

export type RubricVersionStatus = "DRAFT" | "PUBLISHED";
export type QuestionStatus = "DRAFT" | "PUBLISHED";

export interface Rubric {
    id: number;
    name: string;
    description?: string | null;
    active?: boolean | null;
}

export interface CloneRubricPayload {
    name: string;
    description?: string;
    active?: boolean;
    versionIds?: number[];
}

export interface RubricVersion {
    id: number;
    rubricId: number;
    status: RubricVersionStatus;
    majorVersion: string;
    minorVersion: string;
    codeWorkflows?: Record<string, number[]>;
    layoutConfig?: unknown;
    taskConfiguration?: {
        description?: string | null;
    } | null;
    effectiveDate?: string | null;
    expirationDate?: string | null;
    active?: boolean | null;
    procedureWorkflowId?: number | null;
    modifierWorkflowId?: number | null;
    diagnosisWorkflowId?: number | null;
    caseWorkflowId?: number | null;
    spanWorkflowId?: number | null;
    activeScoringConfigId?: number | null;
}

export type CodeWorkflowType = "PROCEDURE" | "MODIFIER" | "DIAGNOSIS" | "CASE" | "SPAN";

export type CodeWorkflowStatus = "DRAFT" | "PUBLISHED";
export type SpanWorkflowQuestionScope = "ALL_SPANS" | "SELECTED_BY_SPANS_QUESTION";
export interface CloneRubricVersionPayload {
    targetRubricId?: number;
    majorVersion?: string;
    minorVersion?: string;
    layoutConfig?: unknown;
    description?: string;
    effectiveDate?: string;
    expirationDate?: string;
    active?: boolean;
}

export enum WorkflowTriggerAction {
    GO_TO = "GO_TO",
    STOP = "STOP",
    SET_ANSWERS = "SET_ANSWERS",
    AUTOFILL = "AUTOFILL",
    SHOW_QUESTION = "SHOW_QUESTION",
}

export type WorkflowTriggerPhase = "PRE" | "POST";

export interface ConditionClause {
    questionId: number;
    answerValueIds: number[];
}

export interface CompoundCondition {
    operator: "AND" | "OR";
    conditions: ConditionClause[];
}

export interface WorkflowTrigger {
    id?: number;
    hostQuestionId?: number;
    fromQuestionId?: number;
    toQuestionId?: number;
    answerValueIds: number[];
    action?: WorkflowTriggerAction;
    triggerPhase?: WorkflowTriggerPhase;
    conditionRule?: string | null;
    toAnswerValueIds?: number[];
    triggerOrder?: number;
    _clientId?: string;
    compoundCondition?: CompoundCondition;
}

export interface WorkflowQuestion {
    id?: number;
    sequenceOrder?: number | null;
    question: Question;
    triggers?: WorkflowTrigger[];
    spanScope?: SpanWorkflowQuestionScope;
    spanScopeQuestionId?: number | null;
}

export interface CodeWorkflow {
    id?: number;
    name: string;
    codeType: CodeWorkflowType | string;
    description?: string | null;
    active?: boolean | null;
    status?: CodeWorkflowStatus;
    majorVersion?: string | null;
    minorVersion?: string | null;
    parentId?: number | null;
    effectiveDate?: string | null;
    expirationDate?: string | null;
    questions?: WorkflowQuestion[];
}

export enum QuestionType {
    MULTIPLE_CHOICE = "MULTIPLE_CHOICE",
    SYSTEM = "SYSTEM",
    FREE_TEXT = "FREE_TEXT",
    SPAN = "SPAN",
    SPANS = "SPANS",
}

export interface Trait {
    traitType: string;
    traitValue: string;
}

export enum InclusionMode {
    INCLUDE = "INCLUDE",
    EXCLUDE = "EXCLUDE",
}

export interface Question {
    id: number;
    name: string;
    questionText: string;
    questionType: QuestionType;
    required: boolean;
    displayOrder?: number | null;
    active?: boolean | null;
    status?: QuestionStatus;
    majorVersion?: string | null;
    minorVersion?: string | null;
    parentId?: number | null;
    answerSetId?: number | null;
    answerSetName?: string | null;
    traits?: Trait[];
    toAnswerValueIds?: number[];
    answers?: AnswerValue[];
    selectedAnswerIds?: number[];
    inclusionMode?: InclusionMode;
    expression?: string | null;
    /** [CDMX-14572] Optional id of a SPAN-typed CodeWorkflow run once per selected span when this question is SPANS-typed. */
    childWorkflowId?: number | null;
}

export interface FullRubricWorkflowDetail {
    workflow: CodeWorkflow;
    questions?: WorkflowQuestion[];
}

export interface FullRubricVersion {
    rubric: Rubric;
    version: RubricVersion;
    workflows: FullRubricWorkflowDetail[];
}
